import type { StudySelection } from './marketplaceApi';

/**
 * Session-scoped draft of the study wizard.
 *
 * The wizard carried ALL of its state in React Router's `location.state`, and the
 * in-progress form content lived only in a component ref that was never persisted
 * until Next was pressed. So a hard reload (or a direct URL) on any step redirected
 * to step 1 and silently discarded the use case, the audience AND the entire typed
 * survey — no warning, no recovery (#73).
 *
 * sessionStorage (not localStorage) on purpose: a draft is scoped to this tab and
 * this sitting. It must not resurrect a half-finished study in a new tab days later.
 */
export interface WizardDraft {
  useCase?: string;
  selections?: StudySelection[];
  twinCount?: number;
  paid?: boolean;
  /** The draft study id, once created — so a restored wizard reuses it instead of
   *  creating a duplicate draft on every reload. */
  studyId?: number;
  /** The use-case form's editable state (question text, concept fields, ...). */
  savedFormData?: Record<string, unknown>;
  updatedAt: number;
}

const KEY = 'mkt.wizard.draft.v1';

/** Stale drafts are worse than none — they silently resurrect an abandoned study. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12h

export function saveDraft(patch: Omit<Partial<WizardDraft>, 'updatedAt'>): void {
  try {
    const current = loadDraft() || {};
    const next: WizardDraft = { ...current, ...patch, updatedAt: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota — the wizard still works, it just won't survive a reload */
  }
}

export function loadDraft(): WizardDraft | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as WizardDraft;
    if (!draft || typeof draft !== 'object') return null;
    if (Date.now() - (draft.updatedAt || 0) > MAX_AGE_MS) {
      clearDraft();
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/** True when the draft carries enough to rebuild a wizard step (use case + audience). */
export function draftIsUsable(draft: WizardDraft | null): draft is WizardDraft {
  return !!draft?.useCase && (draft.selections?.length ?? 0) > 0;
}
